import { InlineAlertType } from '@varo/graphql-schema-types';
import { openSettings } from 'expo-linking';
import {
  AuthenticationType,
  hasHardwareAsync,
  isEnrolledAsync,
  LocalAuthenticationResult,
} from 'expo-local-authentication';
import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useProfilePhoto from 'features/myVaro/hooks/useProfilePhoto';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { isAndroid, isWeb } from 'shared/constants/platform';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import {
  enableBiometrics,
  getBiometricToggleSetting,
  getBiometricType,
  saveBiometricToggleSetting,
  saveHasUserRegistered,
  saveUserIDForReactNative,
} from 'shared/utils/biometricAuth/biometricAuth';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

import sectionListHelper from './PersonalDetails.helpers';

interface APIError {
  code: string;
  message: string;
  title: string;
}

const PersonalDetailsScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'personalDetails',
  });
  const {
    result,
    fetching,
    refetch,
    setErrorScreen,
    errorScreen,
    pickImage,
    bottomSheet,
  } = useProfilePhoto();
  const customerInfo = result?.customerInfo ?? null;
  const isFirstFocus = useRef(true);
  /**
  / refresh = refresh the page to get fresh data.
  / errorKey = define error key in myVaroStrings.personalDetails.errors
  / msgKey = define msg key in myVaroStrings.personalDetails.snackbarMessages
  / apiErrorObj = Error Object return from an API.
  **/
  const { refresh, errorKey, msgKey, apiErrorObj, snackbarMsg } =
    useLocalSearchParams();
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const [isFaceIdEnabled, setIsFaceIdEnabled] = useState<boolean>(false);
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [dialogDetails, setDialogDetails] = useState<{
    title: string;
    message: string;
    buttonsArray: { label: string; onClick: () => void }[];
  } | null>(null);
  const [biometricType, setBiometricType] = useState<
    AuthenticationType[] | null
  >(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  /**
   / Determine snackbar message from route params.
   / Note: snackbarMsg and msgKey are mutually exclusive in practice:
   / - snackbarMsg: Dynamic error messages from API/validation
   / - msgKey: Static success message translation keys
   / Priority order ensures snackbarMsg takes precedence if both are somehow present.
   **/
  const getSnackbarMessage = (): string | null => {
    if (typeof snackbarMsg === 'string') {
      return snackbarMsg;
    }
    if (typeof msgKey === 'string') {
      return translatedText(`snackbarMessages.${msgKey}`);
    }
    return null;
  };

  const snackbarMessage = getSnackbarMessage();

  useFocusEffect(
    useCallback(() => {
      if (msgKey || snackbarMsg) {
        setShowSnackbar(true);
      }
    }, [msgKey, snackbarMsg]),
  );

  const getBiometricToggleStatus = async (): Promise<boolean> => {
    try {
      if (isAndroid) {
        const toggleSetting = await getBiometricToggleSetting();
        const isEnrolled = await isEnrolledAsync();
        return toggleSetting && isEnrolled;
      }
      return await getBiometricToggleSetting();
    } catch (error) {
      logBreadcrumb({
        level: 'error',
        message: `Error checking biometric status: ${error}`,
      });
      return false;
    }
  };

  const checkIsFaceIdRegistered = async () => {
    if (!isWeb) {
      try {
        const biometricType = await getBiometricType();
        if (biometricType) {
          setBiometricType(biometricType);
        }
        const hasRegisteredHardware = await hasHardwareAsync();
        if (hasRegisteredHardware) {
          const biometricToggleStatus = await getBiometricToggleStatus();
          setIsFaceIdEnabled(biometricToggleStatus);
        } else {
          setIsFaceIdEnabled(hasRegisteredHardware);
        }
      } catch (error) {
        logBreadcrumb({
          level: 'error',
          message: `Error checking Face ID registration: ${error}`,
        });
        setIsFaceIdEnabled(false);
      }
    }
  };

  useEffect(() => {
    void checkIsFaceIdRegistered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (customerInfo?.email?.address && refresh === 'true') {
      void saveUserIDForReactNative(customerInfo.email.address);
    }
  }, [customerInfo?.email?.address, refresh]);

  useFocusEffect(
    useCallback(() => {
      const fetchData = () => {
        if (refresh === 'true') {
          refetch('network-only');
        } else if (isFirstFocus.current) {
          refetch('cache-first');
          isFirstFocus.current = false;
        } else {
          refetch();
        }
      };

      void fetchData(); // call the async function
    }, [refetch, refresh]),
  );

  const isFaceRecognition = (type: AuthenticationType[] | null): boolean => {
    return type?.[0] === AuthenticationType.FACIAL_RECOGNITION;
  };

  const getDialogTitle = (
    biometricType: AuthenticationType[] | null,
    translatedText: (key: string) => string,
    baseKey: string,
  ): string => {
    const isFace = isFaceRecognition(biometricType);

    return isFace
      ? translatedText(`${baseKey}.faceIDTitle`)
      : translatedText(`${baseKey}.biometricTitle`);
  };

  const getDialogMessage = (
    biometricType: AuthenticationType[] | null,
    translatedText: (key: string) => string,
    baseKey: string,
  ): string => {
    const isFace = isFaceRecognition(biometricType);

    return isFace
      ? translatedText(`${baseKey}.faceIDMessage`)
      : translatedText(`${baseKey}.biometricMessage`);
  };

  const errorHandler = (result: LocalAuthenticationResult) => {
    if (!result.success && result.error === 'not_available') {
      const dialogButtons = [
        {
          label: translatedText('dialog.buttons.cancel'),
          onClick: () => setIsDialogVisible(false),
        },
        {
          label: translatedText('dialog.buttons.goToSettings'),
          onClick: async () => {
            setIsDialogVisible(false);
            await openSettings();
          },
        },
      ];
      const dialogDetails = {
        title: getDialogTitle(biometricType, translatedText, 'dialog'),
        message: getDialogMessage(biometricType, translatedText, 'dialog'),
        buttonsArray: dialogButtons,
      };
      setDialogDetails(dialogDetails);
      setIsDialogVisible(true);
    } else if (!result.success && result.error === 'not_enrolled') {
      const dialogButtons = [
        {
          label: translatedText('alertDialog.buttons.cancel'),
          onClick: () => setIsDialogVisible(false),
        },
        {
          label: translatedText('alertDialog.buttons.goToSettings'),
          onClick: async () => {
            setIsDialogVisible(false);
            await openSettings();
          },
        },
      ];
      const dialogDetails = {
        title: getDialogTitle(biometricType, translatedText, 'alertDialog'),
        message: getDialogMessage(biometricType, translatedText, 'alertDialog'),
        buttonsArray: dialogButtons,
      };
      setDialogDetails(dialogDetails);
      setIsDialogVisible(true);
    } else if (!result.success && result.error === 'user_cancel') {
      const dialogButtons = [
        {
          label: translatedText('dialog.buttons.ok'),
          onClick: () => setIsDialogVisible(false),
        },
      ];
      const dialogDetails = {
        title: translatedText('dialog.error'),
        message: translatedText('dialog.errorMessage'),
        buttonsArray: dialogButtons,
      };
      void saveHasUserRegistered(true);
      setDialogDetails(dialogDetails);
      setIsDialogVisible(true);
    }
  };

  const handleFaceIdToggle = async (enableFaceId: boolean) => {
    try {
      if (!isWeb && enableFaceId) {
        const enabled = await enableBiometrics(errorHandler);
        if (enabled) {
          setIsFaceIdEnabled(true);
        }
      } else {
        await saveBiometricToggleSetting(false);
        setIsFaceIdEnabled(false);
      }
    } catch {
      // TODO: handling error analytics - JT-121593 // ticket marked as Done
    }
  };

  const { isFeatureEnabled } = useStatsig();

  const isHomeAndMailingAddressFeatureEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_CUSTOMER_SERVICE_HOME_MAILING_ADDRESS,
  );

  const sectionListData = sectionListHelper(
    customerInfo,
    translatedText,
    handleFaceIdToggle,
    isFaceIdEnabled,
    bottomSheet,
    true, // showChangePhoneNumber is now hardcoded to true per feature rollout clean-up
    isHomeAndMailingAddressFeatureEnabled,
  );

  const removeParamKey = (key = 'msgKey') => {
    router.setParams({ [key]: undefined });
  };

  const goBack = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.push('/myvaro');
  };

  useBackHandler(goBack);

  const updateProfilePhotoError =
    typeof errorScreen === 'object' && isCoreError(errorScreen);

  const hasErrorObjParam =
    typeof apiErrorObj === 'object' && apiErrorObj !== null;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          title: translatedText('title'),
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={goBack} />
          ),
        }}
      />
      {showSnackbar && snackbarMessage ? (
        <Snackbar
          text={snackbarMessage}
          buttonOnPress={() => {
            removeParamKey('msgKey');
            removeParamKey('snackbarMsg');
            setShowSnackbar(false);
          }}
          onHide={() => {
            removeParamKey('msgKey');
            removeParamKey('snackbarMsg');
            setShowSnackbar(false);
          }}
        />
      ) : null}
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorScreen ? (
            <DefaultPageDownErrorMessage
              error={errorScreen}
              title={updateProfilePhotoError ? errorScreen.title : defaultTitle}
              message={
                updateProfilePhotoError ? errorScreen.message : defaultMessage
              }
              tryAgainAction={() => {
                setErrorScreen(false);
                refetch();
              }}
            />
          ) : null
        }
      />
      {errorKey || apiErrorObj ? (
        <View style={styles.errorAlert}>
          <InlineAlert
            alertType={InlineAlertType.Error}
            iconKeyType="ERROR"
            titleText={
              hasErrorObjParam
                ? ((JSON.parse(JSON.stringify(apiErrorObj)) as APIError)
                    ?.title as string)
                : translatedText(`errors.${errorKey?.toString()}.title`)
            }
            bodyText={
              hasErrorObjParam
                ? ((JSON.parse(JSON.stringify(apiErrorObj)) as APIError)
                    ?.message as string)
                : translatedText(`errors.${errorKey?.toString()}.description`)
            }
            onDismiss={() => {
              removeParamKey('errorKey');
              removeParamKey('apiErrorObj');
            }}
          />
        </View>
      ) : null}
      <SectionedContentScreen
        maskCellsFromAmplitude
        maskHeaderFromAmplitude
        model={sectionListData}
      />
      <Dialog
        isDialogVisible={isDialogVisible}
        titleText={dialogDetails?.title}
        messageText={dialogDetails?.message}
        dialogButtonProps={dialogDetails?.buttonsArray ?? []}
      />
      <BottomSheet
        contentContainerStyle={styles.bottomSheetPadStyle}
        ref={bottomSheet.ref}
      >
        <ListItem
          onPress={() => pickImage(false)}
          primaryCopy={translatedText('labels.chooseFromLibrary')}
        />
        {!isWeb ? (
          <>
            <View style={styles.hairLine}>
              <HairLineDivider />
            </View>
            <ListItem
              onPress={() => pickImage(true)}
              primaryCopy={translatedText('labels.takePhoto')}
            />
          </>
        ) : null}
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheetPadStyle: {
    paddingTop: 0,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  errorAlert: {
    padding: theme.spacing.m,
  },
  hairLine: {
    marginHorizontal: theme.spacing.m,
  },
});

export default PersonalDetailsScreen;
